import { Link } from "react-router";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { useCategories } from "@/hooks/useCategories";
import { Logo } from "./Logo";
import { ArrowUp, ArrowUpRight, Sparkles, Rss, ShieldCheck, Globe } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { LanguageSelector } from "@/components/ui/LanguageSelector";

const companyLinks = [
  { label: "About Us", href: "/about" },
  { label: "Contact Us", href: "/contact" },
  { label: "Recent Stories", href: "/recent-articles" },
  { label: "Bookmarks", href: "/bookmarks" },
  { label: "All Categories", href: "/categories" },
];

const legalLinks = [
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms & Conditions", href: "/terms" },
  { label: "Disclaimer", href: "/disclaimer" },
];

export function Footer() {
  const { categories } = useCategories();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="mt-20 border-t border-border/70 bg-card/50 backdrop-blur-xl relative overflow-hidden">
      {/* Subtle top ambient aura line matching brand logo spectrum */}
      <div 
        className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent pointer-events-none opacity-60" 
        aria-hidden="true"
      />

      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          
          {/* Col 1: Brand Identity & Mission (5 cols on lg) */}
          <div className="lg:col-span-4 flex flex-col justify-between space-y-6">
            <div className="space-y-4">
              <Logo
                showText
                markClassName="h-11 w-11 transition-transform duration-300 group-hover:scale-105"
                textClassName="text-2xl font-black tracking-tight"
              />
              <p className="text-sm leading-relaxed text-muted-foreground max-w-sm">
                Bloxida delivers concise, intelligent reporting across technology,
                business, design, culture, and lifestyle. Curated for curious minds who value substance over noise.
              </p>
            </div>

            <div className="space-y-4 pt-1">
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/80 px-3 py-1 text-muted-foreground shadow-sm">
                  <Globe className="h-3.5 w-3.5 text-primary" />
                  Global Publication
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 font-medium text-emerald-600 dark:text-emerald-400 shadow-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live Feed Active
                </span>
              </div>

              {/* RSS Publication Channel */}
              <div className="flex items-center gap-2 pt-1">
                <a
                  href="/feed.xml"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-border/60 bg-background/80 text-muted-foreground hover:text-amber-500 hover:border-amber-500/50 hover:bg-amber-500/5 text-xs font-semibold transition-all shadow-sm active:scale-95"
                  aria-label="Subscribe to RSS Feed"
                  title="Subscribe to RSS Feed"
                >
                  <Rss className="h-4 w-4 text-amber-500" />
                  <span>RSS Feed</span>
                </a>
              </div>
            </div>
          </div>

          {/* Col 2: Content Verticals (3 cols on lg) */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-foreground flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Content Verticals
            </h4>

            <ul className="space-y-2.5">
              {categories.length === 0 ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Skeleton className="h-2 w-2 rounded-full" />
                    <Skeleton className="h-4 w-28" />
                  </li>
                ))
              ) : (
                categories.slice(0, 6).map((cat) => (
                  <li key={cat.slug}>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="group inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <span
                        className="h-2 w-2 rounded-full transition-transform duration-200 group-hover:scale-125 shrink-0 shadow-sm"
                        style={{ backgroundColor: cat.color || "hsl(var(--primary))" }}
                      />
                      <span className="group-hover:translate-x-0.5 transition-transform duration-200">
                        {cat.name}
                      </span>
                    </Link>
                  </li>
                ))
              )}
            </ul>

            <div className="pt-2">
              <Link
                to="/categories"
                className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
              >
                Browse all categories <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
          </div>

          {/* Col 3: Company & Governance (2 cols on lg) */}
          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-foreground flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#8A2EE4]" />
              Navigation
            </h4>

            <ul className="space-y-2.5">
              {companyLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="group inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span className="group-hover:translate-x-0.5 transition-transform duration-200">
                      {link.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Newsletter & Daily Briefing (3 cols on lg) */}
          <div className="lg:col-span-3 space-y-4">
            <div className="rounded-2xl border border-border/70 bg-card/80 p-5 shadow-sm space-y-3">
              <div className="space-y-1">
                <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-foreground flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  The Bloxida Brief
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Join 15,000+ executives & thinkers receiving our weekly editorial roundup.
                </p>
              </div>

              <NewsletterSignup source="footer" variant="inline" />

              <p className="text-[11px] text-muted-foreground/70 flex items-center gap-1 pt-1">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                Zero spam. One-click unsubscribe anytime.
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom Sub-bar: Copyright, Back to top & Legal */}
      <div className="border-t border-border/50 bg-background/60">
        <div className="container py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          
          {/* Left: Copyright */}
          <p className="text-center md:text-left font-medium">
            © {new Date().getFullYear()} Bloxida. All rights reserved.
          </p>

          {/* Center: Language Selector & Back to Top */}
          <div className="flex items-center gap-3">
            <LanguageSelector variant="footer" />
            <button
              onClick={scrollToTop}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-border/60 bg-card hover:bg-accent text-xs font-semibold text-foreground transition-all shadow-sm active:scale-95"
              aria-label="Scroll to top of page"
            >
              <ArrowUp className="h-3.5 w-3.5 text-primary" />
              <span>Back to top</span>
            </button>
          </div>

          {/* Right: Legal & Sitemap Links */}
          <div className="flex items-center gap-4 flex-wrap justify-center md:justify-end">
            {legalLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="hover:text-foreground transition-colors font-medium"
              >
                {link.label}
              </Link>
            ))}
            <a
              href="/sitemap.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors font-medium"
            >
              Sitemap
            </a>
          </div>

        </div>
      </div>
    </footer>
  );
}
